import { UUID } from "io-ts-types/UUID";
import React, { createContext, useContext, useReducer } from "react";

import { NpsUserKind } from "@every.org/common/src/routes/nps";

import { logger } from "src/utility/logger";

//#region Types
export enum NpsActionType {
  SHOW_NPS = "SHOW_NPS",
  HIDE_NPS = "HIDE_NPS",
  SET_STEP = "SET_STEP",
  SET_SCORE = "SET_SCORE",
  NEXT_STEP = "NEXT_STEP",
  SET_LAST_NPS_SHOWN_AT = "SET_LAST_NPS_SHOWN_AT",
}

export type NpsStep = 0 | 1 | 2 | 3;

export interface NpsState {
  visible: boolean;
  currentStep: NpsStep;
  userKind: NpsUserKind | null;
  nonprofitId: UUID | null;
  selectedScore: number | null;
  lastNpsShownAt: string | null | undefined; // undefined = not yet loaded from server
}

type NpsAction =
  | {
      type: NpsActionType.SHOW_NPS;
      userKind: NpsUserKind;
      nonprofitId: UUID | null;
    }
  | { type: NpsActionType.HIDE_NPS }
  | { type: NpsActionType.SET_STEP; step: NpsStep }
  | { type: NpsActionType.SET_SCORE; score: number }
  | { type: NpsActionType.NEXT_STEP }
  | { type: NpsActionType.SET_LAST_NPS_SHOWN_AT; value: string | null };
//#endregion

const initialState: NpsState = {
  visible: false,
  currentStep: 0,
  userKind: null,
  nonprofitId: null,
  selectedScore: null,
  lastNpsShownAt: undefined,
};

function npsReducer(state: NpsState, action: NpsAction): NpsState {
  switch (action.type) {
    case NpsActionType.SHOW_NPS:
      return {
        ...initialState,
        visible: true,
        userKind: action.userKind,
        nonprofitId: action.nonprofitId,
      };
    case NpsActionType.HIDE_NPS:
      return { ...state, visible: false };
    case NpsActionType.SET_STEP:
      return { ...state, currentStep: action.step };
    case NpsActionType.SET_SCORE:
      return { ...state, selectedScore: action.score };
    case NpsActionType.NEXT_STEP: {
      if (state.currentStep >= 3) {
        logger.warn({ message: "NPS: cannot advance past step 3" });
        return state;
      }
      return { ...state, currentStep: (state.currentStep + 1) as NpsStep };
    }
    case NpsActionType.SET_LAST_NPS_SHOWN_AT:
      return { ...state, lastNpsShownAt: action.value };
    default:
      return state;
  }
}

export const NpsContext = createContext<NpsState>(initialState);

export let dispatchNpsAction: React.Dispatch<NpsAction>;

export const NpsProvider: React.FCC = ({ children }) => {
  const [npsState, npsDispatch] = useReducer(npsReducer, initialState);
  dispatchNpsAction = npsDispatch;
  return <NpsContext.Provider value={npsState}>{children}</NpsContext.Provider>;
};

export function useNpsContext(): NpsState {
  return useContext(NpsContext);
}
